import { ITERABLE_EVENTS } from 'constants/eventTypes';
import { Combinator, FieldType } from 'modules/queryBuilder/types';
import { EventTypes, BehavioralInsightPresentationMode } from 'modules/domains';
import { Product, Team } from 'teams';
import { pxToRem } from 'utils/convert';
import { ensureEnum } from 'utils/tsHelpers';
import { SegmentedControlOption } from 'ui/segmentedControl';
import { InsightQueryRequest } from './types';

export enum TAB_OPTIONS {
  USERS = 'insights.tabs.users',
  BEHAVIORS = 'insights.tabs.behaviors',
  DEMOGRAPHICS = 'insights.tabs.demographics',
  REFERENCES = 'insights.tabs.references',
}

export enum PAGE_VARIATIONS {
  SEGMENTATION = 'Segmentation',
  STATIC = 'Static',
  DYNAMIC = 'Dynamic',
}

export const FORM_ID = 'segmentation-query-builder';
export const I18N_NAMESPACE = 'segmentation';

export const DOCUMENTATION_URL =
  'https://support.iterable.com/hc/en-us/articles/204780539-Segmentation-Overview';

export const USER_FIELDS_ENDPOINT = '/mappings/userMappings';

export const SEGMENTATION_ENDPOINT = '/segmentation';

export enum STARRED_FIELDS {
  userId = 'userId',
  email = 'email',
}

export const EMAIL_DOMAIN_FIELD = 'itblInternal.emailDomain';

export const PRIORITIZED_COLUMNS = [
  'email',
  'userId',
  'name',
  'firstname',
  'first_name',
  'first name',
  'lastname',
  'last_name',
  'last name',
  'gender',
  'city',
  'state',
  'country',
  'timezone',
];

export const LOCAL_STORAGE_SEGMENTATION_FIELDS_KEY = `ls.segmentationSelectedFields_`;

export const USER_INSIGHTS_RUM_ATTRIBUTES = {
  teamName: Team.AUDIENCE_TEAM,
  productName: Product.AUDIENCE_INSIGHTS,
};

export const SEGMENTATION_RUM_ATTRIBUTES = {
  teamName: Team.AUDIENCE_TEAM,
  productName: Product.SEGMENTATION,
};

/** all iterable events except 'User' and 'CustomEvent' */
export const STANDARD_ITERABLE_EVENTS = Object.values(EventTypes).filter(
  (type) => ![EventTypes.User, EventTypes.CustomEvent].includes(type),
);

/** all standard events organized by name and dataType */
export const ORGANIZED_STANDARD_ITERABLE_EVENTS = Object.values(STANDARD_ITERABLE_EVENTS).map(
  (event) => ({
    event: ITERABLE_EVENTS[event],
    dataType: ensureEnum(event, EventTypes, EventTypes[event]),
  }),
);

export const CUSTOM_BEHAVIOR_PREFIXES = [
  'Total Custom Conversion',
  'Custom Conversion Rate',
  'Conversion Rate',
  'Total Conversions',
];

export const BEHAVIORAL_INSIGHTS_API = '/lists/insights/behavioral';

// Regex capturing content inside parenthesis of behavior key name
export const CUSTOM_BEHAVIOR_REGEX = /^.* \((.+)\)$/;

export enum INSIGHT_PANEL_SIZES {
  small = 'small',
  medium = 'medium',
}

export const BEHAVIORAL_EVENT_SELECT_SEGMENTED_OPTIONS: SegmentedControlOption[] = [
  { name: 'Show total count', value: BehavioralInsightPresentationMode.TotalCount },
  { name: 'Show per user', value: BehavioralInsightPresentationMode.Rate },
];

export const BEHAVIORAL_EVENT_SELECT_WIDTH = pxToRem(350);

export const REMOVE_CUSTOM_INSIGHT_ACTION_ID = 'remove-custom-insight-action';

export const HIGHEST_CONVERSIONS_CAMPAIGNS_TABLES_LENGTH = 5;

export const AUDIENCE_INSIGHTS_METER_FEATURE_FLAG = 'AudienceInsightMeterUI';

export const MODERNIZED_SEGMENTATION_PAGE = 'ModernizedSegmentationPage';

export const AUD_INSIGHTS_TABLES_TRUNCATED_LENGTH = 4;

export const DEFAULT_AUDIENCE_COUNT_NAME = 'unknown';
export const DEFAULT_PAGE = 1;

export const DEFAULT_SEGMENTATION_QUERY = {
  combinator: Combinator.AND,
  searchQueries: [{ combinator: Combinator.AND, searchQueries: [] }],
};

export const MISSING_VALUE = 'unknown';
export const OTHER_VALUE = 'other';

export const DEFAULT_QUERY: InsightQueryRequest = {
  searchQuery: {
    combinator: Combinator.OR,
    searchQueries: [],
  },
};

export const AUD_INSIGHTS_GRID_COL_COUNT = 4;

export const USER_PROFILE_LINK_PREFIX = '/users/profiles/';

export const NONSORTABLE_FIELD_TYPES = [FieldType.OBJECT, FieldType.NESTED, FieldType.GEO_LOCATION];
